import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  variantList,
  variantDetail,
  createVariant,
  updateVariant,
  deleteVariant,
} from "./callbacks";
import { QData } from "../../../types/basic-type-list";
import { IVariant } from "../../../types/api/product";
import { TResponseWithQData } from "../../../types/common";

export const VARIANT_KEYS = {
  all: ["variants"] as const,
  lists: () => [...VARIANT_KEYS.all, "list"] as const,
  list: (qData: QData) => [...VARIANT_KEYS.lists(), qData] as const,
  details: () => [...VARIANT_KEYS.all, "detail"] as const,
  detail: (id: string) => [...VARIANT_KEYS.details(), id] as const,
};

export function useVariantList(
  qData: QData & { productId?: string; search?: string },
  options?: any
) {
  const queryClient = useQueryClient();

  const query = useQuery<TResponseWithQData<IVariant[]>>({
    queryKey: VARIANT_KEYS.list(qData),
    queryFn: async () => {
      const { data } = await variantList(qData);
      return data;
    },
    ...options,
  });

  const refetchWithQuery = (newQData: typeof qData) => {
    queryClient.prefetchQuery({
      queryKey: VARIANT_KEYS.list(newQData),
      queryFn: async () => {
        const { data } = await variantList(newQData);
        return data;
      },
    });
  };

  return { ...query, refetchWithQuery };
}

export function useVariantDetail(id: string, options?: any) {
  return useQuery({
    queryKey: VARIANT_KEYS.detail(id),
    queryFn: async () => {
      const { data } = await variantDetail(id);
      return data;
    },
    enabled: !!id,
    ...options,
  });
}

export function useVariantCreate({
  qData,
  onSuccess,
  onError,
}: {
  qData?: QData;
  onSuccess?: () => void;
  onError?: (err: any) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createVariant,
    onSuccess: (res) => {
      if (qData) {
        queryClient.setQueryData(
          VARIANT_KEYS.list(qData),
          (old: TResponseWithQData<IVariant[]> | undefined) => {
            if (!old) return old;
            return {
              ...old,
              data: [res.data.data, ...(old.data || [])],
              extra: {
                ...old.extra,
                total: (old.extra?.total || 0) + 1,
              },
            };
          }
        );
      } else {
        queryClient.invalidateQueries({ queryKey: VARIANT_KEYS.lists() });
      }
      onSuccess?.();
    },
    onError,
  });
}

export function useVariantUpdate({
  qData,
  onSuccess,
  onError,
}: {
  qData?: QData;
  onSuccess?: () => void;
  onError?: (err: any) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateVariant,
    onSuccess: (res) => {
      const updatedVariant = res.data.data;
      if (qData) {
        queryClient.setQueryData(
          VARIANT_KEYS.list(qData),
          (old: TResponseWithQData<IVariant[]> | undefined) => {
            if (!old) return old;
            return {
              ...old,
              data: old.data?.map((item) =>
                item._id === updatedVariant._id ? updatedVariant : item
              ),
            };
          }
        );
      } else {
        queryClient.invalidateQueries({ queryKey: VARIANT_KEYS.lists() });
      }
      queryClient.setQueryData(VARIANT_KEYS.detail(updatedVariant._id), (old: any) => {
        if (!old) return old;
        return { ...old, data: updatedVariant };
      });
      onSuccess?.();
    },
    onError,
  });
}

export function useVariantDelete({
  qData,
  onSuccess,
  onError,
}: {
  qData?: QData;
  onSuccess?: () => void;
  onError?: (err: any) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteVariant,
    onSuccess: (_, deletedId) => {
      if (qData) {
        queryClient.setQueryData(
          VARIANT_KEYS.list(qData),
          (old: TResponseWithQData<IVariant[]> | undefined) => {
            if (!old) return old;
            return {
              ...old,
              data: old.data?.filter((item) => item._id !== deletedId),
              extra: {
                ...old.extra,
                total: Math.max(0, (old.extra?.total || 1) - 1),
              },
            };
          }
        );
      } else {
        queryClient.invalidateQueries({ queryKey: VARIANT_KEYS.lists() });
      }
      onSuccess?.();
    },
    onError,
  });
}
